import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { API_BASE_URL } from "../config/apiconfig";


const Profile = () => {
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, userType, loading: authLoading } = useAuth();

  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        setLoading(true);
        
        // Wait for auth context to finish loading
        if (authLoading) return;
        
        // If no user is logged in, redirect to login
        if (!user) {
          navigate("/login");
          return;
        }
        
        let profileToDisplay;
        
        // If an ID is specified in the URL, try to fetch that profile
        if (id && id !== user.id) {
          // Check if the user is authorized to view other profiles
          if (userType === 'employee' && !user.isAdmin) {
            setError("You are not authorized to view this profile.");
            setLoading(false);
            return;
          }
          
          // For admins or users with permission, fetch the requested profile
          try {
            // We're already using the auth context which has the user profile
            // So we only need to make a new request if viewing someone else's profile
            const response = await fetch(`${API_BASE_URL}/api/employees/${id}`, {
              headers: {
                'user-id': localStorage.getItem('token')
              }
            });
            
            if (!response.ok) {
              throw new Error('Failed to fetch profile');
            }
            
            profileToDisplay = await response.json();
          } catch (err) {
            console.error("Error fetching employee data:", err);
            setError("Failed to load profile data. Please try again later.");
            setLoading(false);
            return;
          }
        } else {
          // If no ID specified or matches current user, use the profile from auth context
          profileToDisplay = user;
        }
        
        setProfileData(profileToDisplay);
      } catch (err) {
        console.error("Error handling profile data:", err);
        setError("An unexpected error occurred. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchProfileData();
  }, [id, navigate, user, userType, authLoading]);

  if (loading || authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-r from-blue-900 to-blue-700">
        <div className="bg-white p-8 rounded-lg shadow-lg w-full max-w-md text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-700 mx-auto"></div>
          <p className="mt-4 text-gray-700">Loading profile data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-r from-blue-900 to-blue-700">
        <div className="bg-white p-8 rounded-lg shadow-lg w-full max-w-md text-center">
          <div className="text-red-600 text-xl mb-4">Error</div>
          <p className="text-gray-700">{error}</p>
          <button
            onClick={() => navigate("/dashboard")}
            className="mt-4 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition duration-300"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-r from-blue-900 to-blue-700">
        <div className="bg-white p-8 rounded-lg shadow-lg w-full max-w-md text-center">
          <div className="text-red-600 text-xl mb-4">Access Denied</div>
          <p className="text-gray-700">Profile not found.</p>
          <button
            onClick={() => navigate("/dashboard")}
            className="mt-4 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition duration-300"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Format date function
  const formatDate = (dateString) => {
    if (!dateString) return "Not provided";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  // Check if the logged-in user is viewing their own profile
  const isOwnProfile = user && user.id === profileData.id;
  
  // Check if the logged-in user is an admin
  const isAdmin = userType === 'user';

  // Get profile picture URL or use first letter of name for avatar
  const hasProfilePicture = profileData.profilePictureUrl || profileData.profilePicturePath;
  const profilePictureUrl = profileData.profilePictureUrl || 
    (profileData.profilePicturePath ? `${API_BASE_URL}/${profileData.profilePicturePath}` : null);

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-gradient-to-r from-blue-900 to-blue-700 min-h-screen">
      <div className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-6 text-center">
        {isOwnProfile ? "My Profile" : `${userType === 'employee' ? 'Employee' : 'User'} Profile`}
      </div>

      <div className="max-w-5xl mx-auto bg-white rounded-lg shadow-lg overflow-hidden">
        {/* Profile Header */}
        <div className="bg-blue-800 text-white p-6">
          <div className="flex flex-col md:flex-row items-center">
            {hasProfilePicture ? (
              <div className="w-24 h-24 rounded-full mb-4 md:mb-0 md:mr-6 overflow-hidden bg-white">
                <img 
                  src={profilePictureUrl} 
                  alt={`${profileData.name}'s profile`} 
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "";
                    e.target.parentNode.classList.add("bg-blue-500");
                    e.target.parentNode.innerText = profileData.name ? profileData.name.charAt(0).toUpperCase() : "U";
                    e.target.parentNode.classList.add("flex", "items-center", "justify-center", "text-white", "text-3xl", "font-bold");
                  }}
                />
              </div>
            ) : (
              <div className="w-24 h-24 bg-blue-500 rounded-full flex items-center justify-center text-white text-3xl font-bold mb-4 md:mb-0 md:mr-6">
                {profileData.name ? profileData.name.charAt(0).toUpperCase() : "U"}
              </div>
            )}
            <div>
              <h2 className="text-2xl font-bold">{profileData.name || "User"}</h2>
              <p className="text-blue-200">{profileData.designation || "Position not specified"}</p>
              <p className="text-blue-200">{profileData.departmentName || "Department not specified"}</p>
              {profileData.empNo && (
                <div className="mt-2 inline-block px-3 py-1 bg-blue-700 rounded-full text-sm">
                  Employee ID: {profileData.empNo}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Profile Content */}
        <div className="p-6">
          {/* Personal Information */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold mb-4 pb-2 border-b-2 border-blue-200 text-blue-800">
              Personal Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-blue-600 mb-1">Full Name</p>
                <p className="text-gray-800 font-medium">{profileData.name || "Not provided"}</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-blue-600 mb-1">Email</p>
                <p className="text-gray-800 font-medium">{profileData.email || "Not provided"}</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-blue-600 mb-1">Mobile</p>
                <p className="text-gray-800 font-medium">{profileData.mobile || "Not provided"}</p>
              </div>
              
              {/* Only show these fields for employee profiles */}
              {(userType === 'employee' || profileData.nicNumber) && (
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-blue-600 mb-1">NIC Number</p>
                  <p className="text-gray-800 font-medium">{profileData.nicNumber || "Not provided"}</p>
                </div>
              )}
              
              {(userType === 'employee' || profileData.gender) && (
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-blue-600 mb-1">Gender</p>
                  <p className="text-gray-800 font-medium">{profileData.gender || "Not provided"}</p>
                </div>
              )}
              
              {(userType === 'employee' || profileData.maritalStatus) && (
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-blue-600 mb-1">Marital Status</p>
                  <p className="text-gray-800 font-medium">{profileData.maritalStatus || "Not provided"}</p>
                </div>
              )}
              
              {(userType === 'employee' || profileData.birthday) && (
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-blue-600 mb-1">Birthday</p>
                  <p className="text-gray-800 font-medium">{formatDate(profileData.birthday) || "Not provided"}</p>
                </div>
              )}
              
              {(userType === 'employee' || profileData.address) && (
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-blue-600 mb-1">Address</p>
                  <p className="text-gray-800 font-medium">{profileData.address || "Not provided"}</p>
                </div>
              )}
              
              {(userType === 'employee' || profileData.nationalityCode) && (
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-blue-600 mb-1">Nationality</p>
                  <p className="text-gray-800 font-medium">{profileData.nationalityCode || "Not provided"}</p>
                </div>
              )}
            </div>
          </div>

          {/* Employment Information - Only show for employees */}
          {(userType === 'employee' || profileData.empNo) && (
            <div className="mb-8">
              <h3 className="text-xl font-semibold mb-4 pb-2 border-b-2 border-blue-200 text-blue-800">
                Employment Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-blue-600 mb-1">Employee Number</p>
                  <p className="text-gray-800 font-medium">{profileData.empNo || "Not assigned"}</p>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-blue-600 mb-1">Department</p>
                  <p className="text-gray-800 font-medium">{profileData.departmentName || "Not assigned"}</p>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-blue-600 mb-1">Designation</p>
                  <p className="text-gray-800 font-medium">{profileData.designation || "Not assigned"}</p>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-blue-600 mb-1">Join Date</p>
                  <p className="text-gray-800 font-medium">{formatDate(profileData.joinDate) || "Not provided"}</p>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-blue-600 mb-1">User Type</p>
                  <p className="text-gray-800 font-medium">{profileData.userType || userType || "Not specified"}</p>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-blue-600 mb-1">EPF Number</p>
                  <p className="text-gray-800 font-medium">{profileData.epfNumber || "Not provided"}</p>
                </div>
              </div>
            </div>
          )}

          {/* Financial Information - Only show to own user or admin */}
          {((isOwnProfile || isAdmin) && (profileData.salary || profileData.bank || profileData.bankAccNo)) && (
            <div>
              <h3 className="text-xl font-semibold mb-4 pb-2 border-b-2 border-blue-200 text-blue-800">
                Financial Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {profileData.salary && (
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-sm text-blue-600 mb-1">Salary</p>
                    <p className="text-gray-800 font-medium">
                      {`RS.${parseInt(profileData.salary).toLocaleString()}`}
                    </p>
                  </div>
                )}
                {profileData.bank && (
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-sm text-blue-600 mb-1">Bank</p>
                    <p className="text-gray-800 font-medium">{profileData.bank}</p>
                  </div>
                )}
                {profileData.bankAccNo && (
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-sm text-blue-600 mb-1">Bank Account Number</p>
                    <p className="text-gray-800 font-medium">{profileData.bankAccNo}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-100 p-4 text-center text-sm text-gray-600">
          <p>
            Last Updated: {formatDate(profileData.updatedAt)} | Created: {formatDate(profileData.createdAt)}
          </p>
          {/*  <p className="mt-2">
            {isOwnProfile ? (
             <button
                onClick={() => navigate(`/profile/edit/${profileData.id}`)}
                className="text-blue-600 hover:text-blue-800 underline"
              >
                Edit My Profile
              </button> 
            ) : (
              "This information is read-only. Please contact HR if you need to update your details."
            )}
          </p> */}
        </div>
      </div>
    </div>
  );
};

export default Profile;